import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router";
import { Bento } from "@/components/ui/box";
import type { Box, ListBox, TaskBox } from "@/components/ui/box.schema";
import { NativeSelect } from "@/components/ui/native-select";
import { useSessionRoll } from "@/components/attendance/use-session-roll";
import { useRollBox } from "@/components/attendance/use-roll-box";
import { attendanceApi, type CourseAttendance } from "@/lib/api/attendance";
import { coursesApi } from "@/lib/api/courses";
import { classTitle } from "@/lib/class-name";
import { refreshHomeAfterAct } from "@/lib/home-query-keys";
import { SESSION_STATUS_LABEL } from "@/lib/session-portal";
import { isHistoricalTerm } from "@/lib/term-state";
import { registerRead,
  registerText,
  registerPhrases,
  registerCandidate,
} from "@/lib/register-copy";

import { LearnerMarks, type RosterLearner as Learner } from "./LearnerMarks";

export function CourseRoster({ courseId }: { courseId: string }) {
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const courses = useQuery({
    queryKey: ["courses"],
    queryFn: () => coursesApi.list(),
  });
  const course = courses.data?.find(
    (entry) => entry.id === courseId && entry.can_teach === true,
  );
  const historical = isHistoricalTerm(course?.semester_status);
  const timezone = course?.timezone ?? "UTC";
  const query = useQuery({
    queryKey: ["attendance", "course", courseId],
    queryFn: () => attendanceApi.getCourseAttendance(courseId),
    enabled: Boolean(course),
  });
  const ranks = useQuery({
    queryKey: ["people-ranks", courseId],
    queryFn: () => attendanceApi.getPeopleRanks(courseId),
    enabled: Boolean(course),
  });
  const sessions = query.data?.sessions ?? [];
  const requested = params.get("session");
  const next = [...sessions]
    .filter((session) => session.status === "scheduled")
    .sort((a, b) => a.session_date.localeCompare(b.session_date))[0];
  const selected =
    requested === null
      ? (next ?? sessions[sessions.length - 1])
      : sessions.find((session) => session.id === requested);
  const roll = useSessionRoll(
    selected?.id ?? "",
    Boolean(course) && !historical,
  );
  const onSubmitted = () => {
    for (const queryKey of [
      ["attendance", "course", courseId],
      ["people-ranks", courseId],
      ["session-workspace"],
      ["course-home"],
    ])
      void client.invalidateQueries({ queryKey });
    refreshHomeAfterAct(client);
  };
  const { box: rollBox, onOpenChange: onRollOpenChange } = useRollBox({
    roll: roll.data?.session_id === selected?.id ? roll.data : undefined,
    onSubmitted,
  });
  if (courses.isError && !courses.data)
    return (
      <p role="alert">
        {registerText.theRosterIsUnavailable}{" "}
        <button type="button" onClick={() => void courses.refetch()}>
          {registerText.readCoursesAgain}</button>
      </p>
    );
  if (courses.data && !course)
    return (
      <p>
        {registerText.thisRosterIsUnavailable}{" "}
        <Link
          to={
            pathname.startsWith("/admin")
              ? "/admin/teaching/courses"
              : "/instructor/courses"
          }
        >
          {registerText.returnToCourses}</Link>
      </p>
    );
  const requestedLearner = params.get("learner");
  const rosterRead = registerRead(query, registerText.rosters);
  const list: ListBox<CourseAttendance, Learner> = {
    kind: "list",
    id: "teaching-roster",
    job: registerText.readALearnerSAttendance,
    label: registerText.roster,
    owner: { system: "imam" },
    fold: "never",
    read: rosterRead.status === "ready" && !rosterRead.value.roster.length
      ? { status: "empty", sentence: registerText.noLearnersAreEnrolled } : rosterRead,
    detail: (value) =>
      value.roster.length
        ? registerText.readALearnerSMarksInTheirRow
        : registerText.noLearnersAreEnrolled,
    openDetail: (value) => [
      requestedLearner && !value.roster.some((learner) => learner.student_id === requestedLearner) ? registerText.thisLearnerIsNotOnTheRoster : null,
      historical ? registerText.thisTermIsClosedTheRosterStaysReadableAndTakes : !value.sessions.some((session) => session.status === "held") ? registerText.noSessionHasBeenHeldYet : null,
    ].filter(Boolean).join(" ") || undefined,
    state: () => ({ class: "quiet", word: registerText.enrolledLearners }),
    figures: (value) => [
      { value: value.roster.length, unit: registerText.learners },
      {
        value: value.sessions.filter((session) => session.status === "held")
          .length,
        unit: registerText.heldSessions,
      },
    ],
    rows: (value) => value.roster,
    row: {
      key: (learner) => learner.student_id,
      title: (learner) => learner.preferred_name ?? learner.legal_name,
      state: (learner) =>
        learner.stats.absent >= 2
          ? {
              class: "waiting",
              word: registerPhrases.followUpAbsences(learner.stats.absent),
            }
          : null,
      facts: (learner) =>
        registerPhrases.participation(learner.participation_kind === "audit" ? registerText.audit : registerText.credit, learner.stats.present + learner.stats.late, query.data?.sessions.filter((session) => session.status === "held").length ?? 0),
      act: () => ({ does: "open", verb: registerText.readMarks }),
      startsOpen: (learner) => learner.student_id === params.get("learner"),
      // A learner is messaged from People's Private messages, the course's one
      // list of people to message; the roster row is their attendance (#1416).
      detail: (learner) => (
        <div className="grid justify-items-start gap-3">
          <LearnerMarks
            learner={learner}
            held={sessions.filter((session) => session.status === "held")}
            timezone={timezone}
          />
        </div>
      ),
    },
  };
  const boxes: Box[] = [list as Box];
  if (!historical && sessions.length) {
    const chooseSession = {
      id: "roll-session",
      label: registerText.session,
      state: { class: "quiet" as const, word: registerText.selectedSession },
      owner: { system: "imam" as const },
      fold: "never" as const,
      draft: "none" as const,
      render: () => (
        <div className="grid gap-2">
          <NativeSelect
            aria-label={registerText.session}
            value={selected?.id ?? ""}
            onChange={(event) => {
              const nextParams = new URLSearchParams(params);
              nextParams.set("session", event.target.value);
              setParams(nextParams);
            }}
          >
            <option value="" disabled>
              {registerText.selectASession}</option>
            {sessions.map((session) => (
              <option key={session.id} value={session.id}>
                {classTitle(session, timezone)} ·{" "}
                {SESSION_STATUS_LABEL[session.status]}
              </option>
            ))}
          </NativeSelect>
          {requested !== null && !selected && (
            <p role="alert">
              {registerText.thisSessionIsUnavailableInThisCourseChooseAnAvailable}</p>
          )}
        </div>
      ),
    };
    if (rollBox) {
      const task: TaskBox<unknown> = {
        ...rollBox,
        id: "course-roll",
        read: registerRead(roll, registerText.sessionRolls),
        parts: [chooseSession, ...rollBox.parts],
      } as TaskBox<unknown>;
      boxes.push(task as Box);
    } else {
      const reading: TaskBox<CourseAttendance> = {
        kind: "task",
        id: "course-roll",
        job: registerText.chooseAndReadTheRollBeforeMarking,
        label: registerText.roll,
        owner: { system: "imam" },
        fold: "never",
        read: registerRead(query, registerText.rosters),
        detail: () => registerText.readTheSelectedSessionSRollBeforeMarkingIt,
        state: () => ({
          class: roll.isError ? "failed" : "reading",
          word: selected
            ? roll.isError
              ? registerText.rollUnavailable
              : registerText.readingRoll
            : registerText.sessionUnavailable,
        }),
        parts: [
          chooseSession,
          {
            id: "roll",
            label: registerText.marks,
            state: { class: "reading", word: registerText.notRead },
            markedByBox: true,
            owner: { system: "imam" },
            fold: "never",
            draft: "none",
            render: () =>
              selected ? (
                roll.isError ? (
                  <p role="alert">
                    {registerText.theSessionRollCouldNotBeRead}{" "}
                    <button type="button" onClick={() => void roll.refetch()}>
                      {registerText.readThisRollAgain}</button>
                  </p>
                ) : (
                  <p role="status">{registerText.readingTheSessionRoll}</p>
                )
              ) : (
                <p>{registerText.chooseAnAvailableSession}</p>
              ),
          },
        ],
        draft: false,
        result: { state: "none" },
        candidate: { eligible: false, rank: null, blocked: null },
        commit: {
          verb: registerText.recordRoll,
          cap: registerText.whenYouRecordTheRoll,
          effects: () => [],
          blockers: () => [
            {
              kind: "missing",
              part: "roll-session",
              sentence: registerText.readAnAvailableSessionSRollBeforeMarkingIt,
            },
          ],
          run: async () => ({
            state: "refused",
            cap: registerText.notRecorded,
            sentence: registerText.readTheRollFirst,
            draft: "kept",
          }),
        },
      };
      boxes.push(reading as Box);
    }
  }
  return (
    <Bento
      id={`course-people-${courseId}`}
      journey="course-people"
      boxes={boxes.map((box) => box.candidate ? { ...box,
        candidate: registerCandidate(box.candidate, box.id, ranks.isError ? undefined : ranks.data),
      } as Box : box)}
      onOpenChange={(id, open) => { if (id === "course-roll") onRollOpenChange(id, open); }}
    />
  );
}
